import { useRouter } from 'next/router';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { logEvent } from '@/pages/api/v1/utils';
export interface ScreenContextType {
  currentScreen: string;
  direction: 'forward' | 'backward';
  previousScreen: string;
  registeredScreens: string[];
  registerScreen: (screenName: string) => void;
  setScreen: (screenName: string) => void;
}
export interface ScreenProviderProps {
  children: React.ReactNode;
  /**
   * Extra fields merged into every `modal_screen_view` event this provider
   * fires. Lets a modal tag its screen-view events with context the generic
   * provider doesn't know about (e.g. the upsell modal's gate reason).
   */
  eventMetadata?: Record<string, string | undefined>;
  initial?: string;
}
const ScreenContext = createContext<ScreenContextType | undefined>(undefined);
export const useScreenContext = () => {
  const context = useContext(ScreenContext);
  if (!context) {
    throw new Error('useScreenContext must be used within a ScreenProvider');
  }
  return context;
};
export const ScreenProvider = ({
  children,
  initial = '',
  eventMetadata
}: ScreenProviderProps) => {
  const [currentScreen, setCurrentScreen] = useState<string>(initial);
  const [previousScreen, setPreviousScreen] = useState<string>('');
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [registeredScreens, setRegisteredScreens] = useState<string[]>([]);
  const {
    asPath
  } = useRouter();

  // Keep the latest metadata reachable from the screen-change effect without
  // adding it to the deps (an inline object prop would otherwise re-fire the
  // event on every render).
  const eventMetadataRef = useRef(eventMetadata);
  eventMetadataRef.current = eventMetadata;
  useEffect(() => {
    void logEvent({
      event_type: 'modal_screen_view',
      asPath,
      metadata: {
        screen_name: currentScreen,
        ...eventMetadataRef.current
      }
    });
  }, [currentScreen]);
  const registerScreen = useCallback((screenName: string) => {
    setRegisteredScreens(prev => {
      if (!prev.includes(screenName)) {
        return [...prev, screenName];
      }
      return prev;
    });
  }, []);
  const resetParentScroll = (screenName: string) => {
    const screen = document.querySelector(`[data-dialog-screen="${screenName}"]`);
    const screenParent = screen?.parentElement;
    if (screenParent && screenParent.scrollTop > 0) {
      screenParent.scrollTo(0, 0);
    }
  };
  const determineDirection = useCallback((from: string, to: string) => {
    const fromIndex = registeredScreens.indexOf(from);
    const toIndex = registeredScreens.indexOf(to);
    if (fromIndex === toIndex) {
      return;
    }
    if (fromIndex < toIndex) {
      setDirection('forward');
    } else {
      setDirection('backward');
    }
  }, [registeredScreens]);
  const setScreen = useCallback((screenName: string) => {
    // Check screen is registered
    if (!registeredScreens.includes(screenName)) {
      throw new Error(`Screen "${screenName}" is not registered`);
    }
    const from = currentScreen;
    const to = screenName;
    if (from === to) {
      return;
    }
    resetParentScroll(from);
    determineDirection(from, to);
    // This is a hack to ensure the direction is set before the screen changes
    setTimeout(() => {
      setPreviousScreen(from);
      setCurrentScreen(to);
    }, 0);
  }, [currentScreen, determineDirection, registeredScreens]);
  useEffect(() => {
    if (registeredScreens.length > 0 && !currentScreen) {
      // Set the first registered screen as the default
      setCurrentScreen(registeredScreens[0]);
    }
  }, [registeredScreens, currentScreen]);
  const value = useMemo(() => ({
    currentScreen,
    previousScreen,
    setScreen,
    registerScreen,
    direction,
    registeredScreens
  }), [currentScreen, previousScreen, setScreen, registerScreen, direction, registeredScreens]);
  return <ScreenContext.Provider value={value} data-sentry-element="ScreenContext.Provider" data-sentry-component="ScreenProvider" data-sentry-source-file="ScreenContext.tsx">{children}</ScreenContext.Provider>;
};